import {Component, Input, OnChanges, OnDestroy} from '@angular/core';
import {A0, Module, ModuleTitleItem} from '../../../../shared/entities/A0.entity';
import {MapperService} from '../../../../services/mapper.service';
import {SharedModule} from '../../../../shared/shared.module';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {HighlightDirective} from '../../../../shared/highlight.component';

@Component({
  selector: 'modules-title-edition',
  template: `
    <form [formGroup]="titlesForm">
      <div formArrayName="titles">
          @for (titleGroup of titles.controls; track titleGroup.value.id; let index = $index) {
              <div [formGroupName]="index" class="pm-titles mt-3 mb-3" highlightOnOver=".highlight-title-{{titleGroup.value.id}}">
                  <span class="pm-titles-name">Titre {{index+1}}</span>
                  <button (click)="deleteTitle(index)" [matTooltip]="'Supprimer'" class="pm-btn mt-2 me-2 bt-delete" matTooltipPosition="above">
                      <i class="bi bi-trash"></i>
                  </button>
                  <mat-form-field appearance="outline" class="w-100 mt-3 block">
                      <mat-label>Titre</mat-label>
                      <input matInput name="name" required type="text" formControlName="name">
                  </mat-form-field>

                  <div class="mat-form-field-flex">
                      <mat-form-field appearance="outline" style="width:50%">
                          <mat-label>Police</mat-label>
                          <input matInput name="fontSize" required type="number" formControlName="fontSize">
                      </mat-form-field>
                      <mat-checkbox formControlName="shadow">Ombre</mat-checkbox>
                  </div>
              </div>
          }
      </div>
  </form>

  <div class="text-center">
    <button (click)="addTitle()" class="pm-btn mt-2 me-2">
      Ajouter un nouveau titre
    </button>
  </div>
  `,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    HighlightDirective
  ]
})
export class ModuleTitleEditionComponent implements OnChanges, OnDestroy {

  @Input() plan: A0;
  @Input() listModule: Module[];

  public titlesForm: FormGroup;
  private formSubscription: Subscription;
  public initialTitles: ModuleTitleItem[] = [];

  constructor(
    private fb: FormBuilder,
    public mapperService: MapperService,
    public snackbar: MatSnackBar,
  ) {
    this.titlesForm = this.fb.group({
      titles: this.fb.array([])
    });
  }

  get titles(): FormArray {
    return this.titlesForm.get('titles') as FormArray;
  }

  private createTitleFormGroup(title: ModuleTitleItem): FormGroup {
    return this.fb.group({
      id: [title.id],
      name: [title.name, Validators.required],
      fontSize: [title.fontSize, [Validators.required, Validators.min(1)]],
      shadow: [title.shadow]
    });
  }

  ngOnChanges(): void {
    if (this.plan) {
      this.mapperService.getListTitles(this.plan).subscribe((titles) => {
        this.initialTitles = titles;
        this.setTitlesFormArray(titles);
        this.subscribeToFormChanges();
      });
    }
  }

  private setTitlesFormArray(titles: ModuleTitleItem[]): void {
    const titlesFGs = titles.map(title => this.createTitleFormGroup(title));
    const titlesArray = this.fb.array(titlesFGs);
    this.titlesForm.setControl('titles', titlesArray);
  }

  private subscribeToFormChanges(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    this.formSubscription = this.titles.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    ).subscribe((updatedTitles: Partial<ModuleTitleItem>[]) => {
      updatedTitles.forEach((updatedTitle, index) => {
        const originalTitle = this.initialTitles.find(t => t.id === updatedTitle.id);

        if (originalTitle &&
          (originalTitle.name !== updatedTitle.name ||
            originalTitle.fontSize !== updatedTitle.fontSize ||
            originalTitle.shadow !== updatedTitle.shadow)) {

          const titleToUpdate: ModuleTitleItem = {
            ...originalTitle,
            ...updatedTitle
          } as ModuleTitleItem;

          this.mapperService.createOrUpdateTitle(this.plan, titleToUpdate).subscribe({
            next: (e) => {
              this.initialTitles[index] = e;
              this.snackbar.open('Titre mis à jour', 'Fermer', {duration: 2000});
            },
            error: () => {
              this.snackbar.open('Erreur lors de la mise à jour', 'Fermer', {duration: 2000});
              this.titles.at(index).patchValue(originalTitle, { emitEvent: false });
            }
          });
        }
      });
    });
  }

  deleteTitle(index: number): void {
    const formGroup = this.titles.at(index) as FormGroup;
    const titleToDelete = formGroup.value as ModuleTitleItem;

    if (titleToDelete.id) {
      this.mapperService.deleteTitle(this.plan, titleToDelete).subscribe(() => {
        this.snackbar.open('Titre supprimé', 'Fermer', {duration: 2000});

        this.titles.removeAt(index);

        this.initialTitles = this.initialTitles.filter(t => t.id !== titleToDelete.id);
      });
    }
  }

  addTitle(): void {
    const newTitlePartial: Partial<ModuleTitleItem> = {
      name: 'Nouveau Titre ' + (this.initialTitles.length + 1),
      position: '0,0',
      color: '#ffffff',
      fontSize: 20,
      shadow: true,
    };

    this.mapperService.createOrUpdateTitle(this.plan, newTitlePartial as ModuleTitleItem).subscribe((newTitle) => {
      this.snackbar.open('Nouveau titre ajouté', 'Fermer', {duration: 2000});

      const newFormGroup = this.createTitleFormGroup(newTitle);
      this.titles.push(newFormGroup);

      this.initialTitles.push(newTitle);
    });
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }
}
